/**
 * <footer-newsletter> — footer newsletter form; Klaviyo client subscription when configured.
 *
 * Loaded ONLY from `blocks/footer-newsletter.liquid` via
 * `type="module" fetchpriority="low"`.
 *
 * Dependencies:
 *   - @theme/delegate
 *   - @theme/form-validator
 */

import { Delegate } from '@theme/delegate';
import { FormValidator } from '@theme/form-validator';

class FooterNewsletter extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
    this.delegateElement = null;
    this.validator = null;
    this.newsletterAbortControllers = new Map();
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.#dropCustomerFormParams();

    this.delegateElement = new Delegate(this);
    this.delegateElement.on(
      'submit',
      '.main-footer__newsletter-customer-form',
      this.#onNewsletterSubmit,
    );
    this.delegateElement.on(
      'click',
      '.main-footer__newsletter-button',
      this.#onKlaviyoButtonClick,
    );
    this.delegateElement.on(
      'keydown',
      '.main-footer__newsletter-input',
      this.#onKlaviyoInputKeydown,
    );
    this.delegateElement.on(
      'click',
      '.main-footer__newsletter-clear-button',
      this.#onNewsletterClear,
    );
    this.delegateElement.on(
      'input',
      '.main-footer__newsletter-input',
      this.#clearSubmissionError,
    );
    this.#initFormValidation();
  }

  disconnectedCallback() {
    this.initialized = false;
    this.delegateElement?.off();
    this.delegateElement = null;
    this.validator?.destroy();
    this.validator = null;
    this.abortNewsletterRequests();
  }

  // A signup that reaches Shopify natively (no JS yet, or no Klaviyo keys) posts to
  // /contact and lands back on `?form_type=customer&utf8=✓&contact[tags]=newsletter`.
  // That render carries the success note, but the params stay in the address bar and
  // Shopify re-reads them on the next plain GET of the URL — now as a signup with no
  // email — so the field returns red with an error moments after the visitor was told
  // they were subscribed (869ebrznn). A reload, back/forward or a market switch was
  // enough. Nothing reads these params after the first render, so drop them.
  #dropCustomerFormParams() {
    const url = new URL(window.location.href);
    if (url.searchParams.get('form_type') !== 'customer') return;

    for (const key of [...url.searchParams.keys()]) {
      if (key === 'form_type' || key === 'utf8' || key === 'contact' || key.startsWith('contact[')) {
        url.searchParams.delete(key);
      }
    }

    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${url.search}${url.hash}`,
    );
  }

  #initFormValidation() {
    const form = this.querySelector('.main-footer__newsletter-customer-form');
    const emailInput = form?.querySelector('.main-footer__newsletter-input');
    if (!form || !emailInput) return;

    form.noValidate = true;
    this.validator = new FormValidator(form, {
      errorClass: 'main-footer__newsletter-row--error',
      fieldSelector: '.main-footer__newsletter-row',
      validateOnBlur: true,
      validateOnInput: true,
      errorOutputSelector: form.querySelector('.main-footer__newsletter-error'),
      showErrorsOnSubmitOnly: false,
    });

    if (emailInput.id) {
      const invalidMessage = emailInput.dataset.invalidMessage || 'Invalid e-mail format';
      this.validator.addValidator(emailInput.id, (value) => {
        // Empty is handled by `required` on submit — do not treat it as an
        // invalid-format error (that message was flashing after success clears).
        if (!value.trim()) return true;
        return this.validator.isValidEmail(value) ? true : invalidMessage;
      });
    }
  }

  #clearSubmissionError = (_event, input) => {
    const form = input.closest('.main-footer__newsletter-customer-form');
    const message = form?.querySelector('.main-footer__newsletter-message--error');
    message?.remove();
  };

  #onKlaviyoButtonClick = (event, button) => {
    const container = button.closest('[data-newsletter-provider="klaviyo"]');
    if (!container) return;

    event.preventDefault();
    this.#onNewsletterSubmit(event, container);
  };

  #onKlaviyoInputKeydown = (event, input) => {
    if (event.key !== 'Enter' || event.isComposing || event.repeat) return;

    const container = input.closest('[data-newsletter-provider="klaviyo"]');
    if (!container) return;

    event.preventDefault();
    this.#onNewsletterSubmit(event, container);
  };

  #onNewsletterClear = (_event, button) => {
    const form = button.closest('.main-footer__newsletter-customer-form');
    const emailInput = form?.querySelector('.main-footer__newsletter-input');
    if (!form || !emailInput) return;

    emailInput.value = '';
    form.querySelector('.main-footer__newsletter-message--error')?.remove();
    this.validator?.clearErrors();
    emailInput.focus();
  };

  abortNewsletterRequests() {
    this.newsletterAbortControllers.forEach((controller) => controller.abort());
    this.newsletterAbortControllers.clear();
  }

  #refreshNewsletterAbortController(form) {
    this.newsletterAbortControllers.get(form)?.abort();

    const controller = new AbortController();
    this.newsletterAbortControllers.set(form, controller);

    return controller;
  }

  #isActiveNewsletterRequest(form, controller) {
    return (
      this.isConnected &&
      form?.isConnected &&
      !controller.signal.aborted &&
      this.newsletterAbortControllers.get(form) === controller
    );
  }

  #getKlaviyoPayload(email, listId) {
    return {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Shopify Footer Newsletter',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
                subscriptions: {
                  email: {
                    marketing: {
                      consent: 'SUBSCRIBED',
                    },
                  },
                },
              },
            },
          },
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: listId,
            },
          },
        },
      },
    };
  }

  #setNewsletterMessage(form, message, isError = false) {
    let messageElement = form.querySelector('.main-footer__newsletter-message');
    if (!messageElement) {
      messageElement = document.createElement('p');
      messageElement.className = 'main-footer__newsletter-message u-p3';
      form.appendChild(messageElement);
    }
    if (isError) {
      messageElement.setAttribute('role', 'alert');
      messageElement.setAttribute('aria-live', 'assertive');
    } else {
      messageElement.setAttribute('role', 'status');
      messageElement.setAttribute('aria-live', 'polite');
    }
    messageElement.setAttribute('aria-atomic', 'true');
    messageElement.textContent = message;
    messageElement.classList.toggle('main-footer__newsletter-message--error', isError);
  }

  #setValidationError(form, message) {
    const errorElement = form.querySelector('.main-footer__newsletter-error');
    const newsletterRow = form.querySelector('.main-footer__newsletter-row');

    if (errorElement) {
      errorElement.textContent = message;
      newsletterRow?.classList.add('main-footer__newsletter-row--error');
      return;
    }

    this.#setNewsletterMessage(form, message, true);
  }

  #setNewsletterLoadingState(form, isLoading) {
    const submitButton = form.querySelector('.main-footer__newsletter-button');
    if (submitButton) {
      submitButton.disabled = isLoading;
      submitButton.setAttribute('aria-busy', String(isLoading));
    }
  }

  #hideNewsletterRow(form) {
    const newsletterRow = form.querySelector('.main-footer__newsletter-row');
    if (newsletterRow) {
      newsletterRow.hidden = true;
    }
    // Success state replaces the input entirely — also drop the GDPR text.
    const gdpr = form.querySelector('.main-footer__newsletter-gdpr');
    if (gdpr) {
      gdpr.hidden = true;
    }
  }

  #showNewsletterSuccess(form, message) {
    form.classList.add('is-success');
    form.setAttribute('data-newsletter-success', 'true');

    // Stop live validation before clearing the field — emptying a required email
    // would otherwise re-run FormValidator and paint "Invalid e-mail format" under
    // the success message via the still-visible .main-footer__newsletter-error.
    this.validator?.clearErrors();
    this.validator?.destroy();
    this.validator = null;

    const errorElement = form.querySelector('.main-footer__newsletter-error');
    if (errorElement) {
      errorElement.textContent = '';
      errorElement.hidden = true;
    }

    this.#hideNewsletterRow(form);
    this.#setNewsletterMessage(form, message);

    const emailInput = form.querySelector('.main-footer__newsletter-input');
    if (emailInput) {
      emailInput.removeAttribute('required');
      emailInput.value = '';
    }
  }

  #isAlreadySubscribedPayload(responseText = '') {
    if (!responseText) return false;

    const hasAlreadySubscribedText = (text = '') => {
      const normalized = text.toLowerCase();
      return (
        normalized.includes('already subscribed') ||
        normalized.includes('already in list') ||
        normalized.includes('already exists') ||
        normalized.includes('duplicate') ||
        normalized.includes('already consented')
      );
    };

    try {
      const parsed = JSON.parse(responseText);
      const errors = Array.isArray(parsed?.errors) ? parsed.errors : [];
      const errorText = errors
        .map((item) => [item?.code, item?.title, item?.detail].filter(Boolean).join(' '))
        .join(' ');
      return hasAlreadySubscribedText(errorText);
    } catch {
      return hasAlreadySubscribedText(responseText);
    }
  }

  #getFormConfig(form) {
    return {
      klaviyoEnabled:
        form.querySelector('.main-footer__klaviyo-enabled')?.value?.trim() === 'true',
      apiKey: form.querySelector('.main-footer__klaviyo-api-key')?.value?.trim() || '',
      listKey: form.querySelector('.main-footer__klaviyo-list-key')?.value?.trim() || '',
      successMessage:
        form.querySelector('.main-footer__newsletter-success-message')?.value?.trim() || '',
      alreadySubscribedMessage:
        form.querySelector('.main-footer__newsletter-already-message')?.value?.trim() || '',
      unavailableMessage:
        form.querySelector('.main-footer__newsletter-unavailable-message')?.value?.trim() || '',
      invalidEmailMessage:
        form.querySelector('.main-footer__newsletter-invalid-message')?.value?.trim() || '',
      requestFailedMessage:
        form.querySelector('.main-footer__newsletter-request-failed-message')?.value?.trim() || '',
    };
  }

  #getSubscribedEmailsStorageKey(listKey) {
    return `main-footer-klaviyo-subscribed:${listKey}`;
  }

  #getStoredSubscribedEmails(listKey) {
    try {
      const rawValue = window.localStorage.getItem(this.#getSubscribedEmailsStorageKey(listKey));
      if (!rawValue) return [];
      const parsed = JSON.parse(rawValue);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  #isEmailStoredAsSubscribed(listKey, email) {
    const normalizedEmail = email.toLowerCase();
    return this.#getStoredSubscribedEmails(listKey).includes(normalizedEmail);
  }

  #storeSubscribedEmail(listKey, email) {
    const normalizedEmail = email.toLowerCase();
    const existingEmails = this.#getStoredSubscribedEmails(listKey);
    if (existingEmails.includes(normalizedEmail)) return;

    try {
      window.localStorage.setItem(
        this.#getSubscribedEmailsStorageKey(listKey),
        JSON.stringify([...existingEmails, normalizedEmail]),
      );
    } catch {
      // Ignore storage quota/privacy mode issues and proceed silently.
    }
  }

  #onNewsletterSubmit = async (event, form) => {
    if (form.classList.contains('is-success') || form.dataset.newsletterSuccess === 'true') {
      event.preventDefault();
      return;
    }

    const formConfig = this.#getFormConfig(form);
    const emailInput = form.querySelector('.main-footer__newsletter-input');
    const email = emailInput?.value?.trim() || '';

    // Klaviyo-enabled forms must never fall through to Shopify's native POST.
    // Prevent synchronously before validation or any other code can fail.
    if (formConfig.klaviyoEnabled) {
      event.preventDefault();

      // Ignore a repeated click/Enter while the active request owns this form.
      if (this.newsletterAbortControllers.has(form)) return;
    }

    const validatorValid = this.validator?.showAllErrors() ?? true;
    const emailValid = email !== '' && Boolean(emailInput?.checkValidity?.());

    // Guard both submission paths directly. The shared validator owns the
    // visual field state, while this native constraint check guarantees an
    // empty/malformed address never reaches Shopify if validation setup fails.
    if (!validatorValid || !emailValid) {
      event.preventDefault();

      this.#setValidationError(form, formConfig.invalidEmailMessage);
      return;
    }

    if (!formConfig.klaviyoEnabled) return;

    const { apiKey, listKey } = formConfig;
    if (!apiKey || !listKey) {
      this.#setNewsletterMessage(form, formConfig.unavailableMessage, true);
      return;
    }

    if (this.#isEmailStoredAsSubscribed(listKey, email)) {
      this.#showNewsletterSuccess(form, formConfig.alreadySubscribedMessage);
      return;
    }

    const payload = this.#getKlaviyoPayload(email, listKey);
    const controller = this.#refreshNewsletterAbortController(form);
    this.#setNewsletterLoadingState(form, true);

    try {
      const requestUrl = `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(
        apiKey,
      )}`;

      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          Accept: 'application/vnd.api+json',
          revision: '2025-04-15',
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      const responseText = await response.text();
      if (!this.#isActiveNewsletterRequest(form, controller)) return;

      const isAlreadySubscribed =
        response.status === 409 || this.#isAlreadySubscribedPayload(responseText);

      if (!response.ok) {
        if (isAlreadySubscribed) {
          this.#storeSubscribedEmail(listKey, email);
          this.#showNewsletterSuccess(form, formConfig.alreadySubscribedMessage);
          return;
        }
        throw new Error(`Klaviyo request failed with status ${response.status}`);
      }

      this.#storeSubscribedEmail(listKey, email);
      this.#showNewsletterSuccess(
        form,
        isAlreadySubscribed ? formConfig.alreadySubscribedMessage : formConfig.successMessage,
      );
    } catch (error) {
      if (error.name === 'AbortError') return;
      if (!this.#isActiveNewsletterRequest(form, controller)) return;

      this.#setNewsletterMessage(form, formConfig.requestFailedMessage, true);
      console.error('[footer-newsletter / Klaviyo] Submit failed', error);
    } finally {
      if (this.newsletterAbortControllers.get(form) === controller) {
        this.newsletterAbortControllers.delete(form);

        if (this.isConnected && form.isConnected && !form.classList.contains('is-success')) {
          this.#setNewsletterLoadingState(form, false);
        }
      }
    }
  };
}

if (!customElements.get('footer-newsletter')) {
  customElements.define('footer-newsletter', FooterNewsletter);
}
